import type { Infrastructure } from '@/models/interfaces/Infrastructure'
import { apiClient } from '@/plugins/axios/api'

const INFRASTRUCTURES_BASE = '/api/infrastructures'

export interface AttributeValueItem {
  pkFkAtgId: number | null
  pkFkIftId: number | null
  attributName: string | null
  infrastructureName: string | null
  aglValTxtcourt: string | null
  aglValNum: number | null
  aglValDate: string | null
  aglValBool: boolean | null
  aglStatut: number | null
  fkInstanceId: number | null
  updatedAt: string | null
}

export const InfrastructureService = {
  /**
   * GET /api/infrastructures/{ift_id}
   * Retrieves a single Infrastructure resource. id must be the infrastructure's ift_id.
   * @param bustCache - when true, appends a query param so the response is not served from cache (e.g. after saving attribute values).
   */
  getById(iftId: string | number, bustCache = false): Promise<Infrastructure> {
    const url = `${INFRASTRUCTURES_BASE}/${encodeURIComponent(String(iftId))}`
    const config = bustCache ? { params: { _: Date.now() } } : undefined
    return apiClient.get(url, config).then((response) => response.data as Infrastructure)
  },

  /**
   * GET /api/infrastructures
   * Retrieves the collection of Infrastructure resources.
   */
  getAll(): Promise<Infrastructure[]> {
    return apiClient
      .get(INFRASTRUCTURES_BASE)
      .then((response) => (response.data['hydra:member'] as Infrastructure[]) ?? [])
  },

  /**
   * PUT /api/infrastructures/{ift_id}
   * Replaces the Infrastructure resource.
   */
  update(iftId: string | number, data: Partial<Infrastructure>): Promise<Infrastructure> {
    return apiClient
      .put(`${INFRASTRUCTURES_BASE}/${encodeURIComponent(String(iftId))}`, data)
      .then((response) => response.data as Infrastructure)
  },

  /**
   * GET /api/infrastructures/{ift_id}/attribute_values
   * Returns the list of attribute values for the infrastructure. Used to know total attribute count for popup (e.g. "X / Y attributes").
   * Response shape is backend-dependent (object of key-value pairs or collection); we return the raw data and count entries.
   */
  getAttributeValues(iftId: string | number): Promise<Record<string, unknown> | unknown[]> {
    const url = `${INFRASTRUCTURES_BASE}/${encodeURIComponent(String(iftId))}/attribute_values`
    return apiClient.get(url).then((response) => {
      const data = response.data
      if (data == null) return []
      if (Array.isArray(data)) return data
      if (typeof data === 'object' && !Array.isArray(data)) return data as Record<string, unknown>
      return []
    })
  },

  /**
   * PUT /api/infrastructures/{ift_id}/attribute_values
   * Upserts attribute values for the infrastructure (inst_atg_ift). Backend runs refresh_all_indicators() after success.
   * Backend returns 204 No Content on success (no body). API Platform accepts application/ld+json only (not application/json).
   */
  putAttributeValues(
    iftId: string | number,
    payload: Record<string, string | number | boolean>,
  ): Promise<Infrastructure | void> {
    return apiClient
      .put(
        `${INFRASTRUCTURES_BASE}/${encodeURIComponent(String(iftId))}/attribute_values`,
        payload,
        { headers: { 'Content-Type': 'application/ld+json' } },
      )
      .then((response) => {
        if (response.status === 204 || response.data == null || response.data === '') {
          return undefined
        }
        return response.data as Infrastructure
      })
  },

  /**
   * GET /api/infrastructures/attribute_values
   * Returns all attribute values for all infrastructures as a flat collection.
   */
  getAllAttributeValues(): Promise<AttributeValueItem[]> {
    return apiClient
      .get(`${INFRASTRUCTURES_BASE}/attribute_values`)
      .then((response) => {
        const data = response.data
        if (Array.isArray(data)) return data as AttributeValueItem[]
        if (data?.['hydra:member']) return data['hydra:member'] as AttributeValueItem[]
        return []
      })
  },
}
